import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

interface User {
  Name: string;
  Email: string;
  UserId?: number;
  EntityUserRoleId?: number;
  RoleCd?: string;
  EntityType?: string;
  EntityId?: number;
}

interface Owner {
  OrderParentId: number;
  OwnerUserId: number;
  OwnerName: string;
  OwnerEmail: string;
}

interface SharedUser {
  result: User[];
  owner: Owner[];
}

interface UserListState {
  isLoading: boolean;
  data: SharedUser | null;
  error: string | null;
}

export interface ResponseError {
  message: string;
}

export interface RequestObject {
  Action: string;
  OrderId: number;
  SharedUsers?: User[];
}

interface UserAccess {
  UserId: number;
  UserAction: string;
}

export interface UpdateEstimateRequestObject {
  Action: string;
  OrderId: number;
  SharedUsers: UserAccess[];
}

const initialState: UserListState = {
  isLoading: true,
  data: null,
  error: null,
};

export const fetchSharedUserListData = createAsyncThunk<
  any,
  RequestObject,
  { rejectValue: ResponseError }
>(
  "ShareEstimateSlice/fetchSharedUserListData",
  async (requestObject, thunkApi) => {
    try {
      const response = await httpClient.post("/order", requestObject);
      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to fetch shared user list.",
          });
        }
      }
      return response.data;
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to fetch shared user list.",
      });
    }
  }
);

export const shareEstimate = createAsyncThunk<any, RequestObject>(
  "ShareEstimateSlice/shareEstimate",
  async (payload, thunkApi) => {
    try {
      const response = await httpClient.post("/order", payload);
      if (response.status !== 200 || response.data.Status !== "success") {
        return thunkApi.rejectWithValue({
          message: response.data.Message || "Failed to share Transaction",
        });
      }
      return response.data;
    } catch (error: any) {
      if (error.status == 400) {
        return thunkApi.rejectWithValue({
          message: error.response.data.Message || "Failed to share Transaction",
        });
      }
      return thunkApi.rejectWithValue({
        message: error?.message || "Failed to share Transaction",
      });
    }
  }
);

export const updateEstimateAccess = createAsyncThunk<
  any,
  UpdateEstimateRequestObject
>("ShareEstimateSlice/updateEstimateAccess", async (payload, thunkApi) => {
  try {
    const response = await httpClient.post("/order", payload);
    if (response.status !== 200 || response.data.Status !== "success") {
      return thunkApi.rejectWithValue({
        message: response.data.Message || "Failed to update estimate access",
      });
    }
    return response.data;
  } catch (error: any) {
    if (error.status == 400) {
      return thunkApi.rejectWithValue({
        message:
          error.response.data.Message || "Failed to update estimate access",
      });
    }
    return thunkApi.rejectWithValue({
      message: error?.message || "Failed to update estimate access",
    });
  }
});

const ShareEstimateSlice = createSlice({
  name: "ShareEstimateSlice",
  initialState,
  reducers: {
    resetSharedUserList: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSharedUserListData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchSharedUserListData.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        }
      )
      .addCase(
        fetchSharedUserListData.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error =
            action.payload.message || "Failed to fetch shared user list.";
        }
      );
  },
});

export const { resetSharedUserList } = ShareEstimateSlice.actions;

export default ShareEstimateSlice.reducer;
